import React from 'react';
import Step from './Step';
import ExpandableContainer from './ExpandableContainer';
import HeatExchangerRequirements from './HeatExchangerRequirements'
import * as Row from '../../layout/Row';
import Field from '../../../model/configurator/Field';
import StepModel from '../../../model/configurator/StepModel';
import QuickStepSetupField from '../../../model/configurator/heating/QuickStepSetupField';
import HeatExchangerField from '../../../model/configurator/heating/HeatExchangerField';
import IsSparePumpField from '../../../model/configurator/heating/IsSparePumpField';
import IsPumpSparePartField from '../../../model/configurator/heating/IsPumpSparePartField';
import SpareCartridgeField from '../../../model/configurator/heating/SpareCartridgeField';
import {GetPumpLiftingHeightTips} from './helpers/heatingHelpers';
import store from '../../../store/configuration';
import PumpAdditionalFilters from './common/PumpAdditionalFilters';
import HeatPumpConnections from './common/HeatPumpConnections';

export class StepHeatingBase extends Step {
    state = {
        pumpAdditionalFilterSectionVisible: false
    }

    constructor(props) {
        super(props)

        this.canIgnoreResponse = true
        this.responsesToIgnore = 0
        this.calculationUpdateTimeout = -1
    }

    renderBody(model) {
        let heatingModel = model['heating' + this.getHeatingIndex()]
        let secondaryPipeSizeTips = this.doFieldsHaveValues(heatingModel.secondaryPipeDN) ? [`${__('DN')}: ${heatingModel.secondaryPipeDN.value}`] : []

        return (
            <React.Fragment>
                <div className="row row-no-outer-gutter flex-center-v spaced-items-group-xs"
                     style={{marginBottom: '1rem'}}>
                    <div className="spaced-item column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.capacity)}
                    </div>

                    <div className="spaced-item column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.quickStepSetup)}
                    </div>
                </div>

                <HeatExchangerRequirements
                    model={heatingModel}
                    primary={this.fields.primary}
                    secondary={this.fields.secondary}
                    plateType={this.fields.plateType}
                    numberOfPlates={this.fields.numberOfPlates}
                    margin={this.fields.margin}
                    marginLock={this.fields.marginLock}
                    renderField={this.renderFieldv2}>

                    <HeatExchangerRequirements.LeftSideUnderExchangerSides>
                        <div style={{
                            marginLeft: '60px',
                            marginBottom: '10px'
                        }}>{this.renderFieldv2(this.fields.secondarySideIncluded)}</div>
                        <div
                            style={{marginLeft: '60px'}}>{this.renderFieldv2(this.fields.heatPumpConnectionsIncluded)}</div>
                        <div
                            style={{marginTop: '10px'}}>{this.renderGenericHorizontalField(this.fields.heatPumpConnectionType)}</div>
                    </HeatExchangerRequirements.LeftSideUnderExchangerSides>

                    <HeatExchangerRequirements.RightSideUnderExchangerSides>
                        {this.renderGenericHorizontalField(this.fields.secondaryPipeSize, {}, secondaryPipeSizeTips)}
                        {this.renderGenericHorizontalField(this.fields.secSidePipeModulePipeSize)}
                        {!this.shouldShowSafetyValveInSecondarySideComponents() && this.renderGenericHorizontalField(this.fields.safetyValve)}
                        {!this.shouldShowFillingInSecondarySideComponents() && this.renderGenericHorizontalField(this.fields.filling)}
                        {!this.shouldShowWaterMeterCommunicationInSecondarySideComponents() && this.renderGenericHorizontalField(this.fields.waterMeterCommunication)}
                    </HeatExchangerRequirements.RightSideUnderExchangerSides>
                </HeatExchangerRequirements>

                {this.fields.secondarySideIncluded.value ? (
                    <React.Fragment>
                        {this.renderSection(
                                this.fields.valveSupply,
                                this.fields.filling,
                                this.fields.waterMeterCommunication,
                                this.fields.deaerator,
                                this.fields.vacuumDGSR,
                                this.fields.manometer,
                                this.fields.expansionSectionServiceShutOff,
                                this.fields.safetyValve,
                                this.fields.strainerOption)
                            && this.renderSecondarySideComponents(heatingModel)}

                        <HeatPumpConnections fields={this.fields}
                                             renderGenericHorizontalField={this.renderGenericHorizontalField}/>

                        {this.renderPump(heatingModel)}
                    </React.Fragment>
                ) : ''}
            </React.Fragment>
        )
    }

    shouldShowFillingInSecondarySideComponents() {
        return this.props.model.unitType.isMaxiCompactLogic.value != true && this.props.model.unitType.isWD40Logic.value != true;
    }

    shouldShowWaterMeterCommunicationInSecondarySideComponents() {
        return this.props.model.unitType.isMaxiCompactLogic.value != true && this.props.model.unitType.isWD40Logic.value != true;
    }

    shouldShowSafetyValveInSecondarySideComponents() {
        return this.props.model.unitType.isMaxiCompactLogic.value != true && this.props.model.unitType.isWD40Logic.value != true;
    }

    renderSecondarySideComponents(model) {
        const warnings = [
            this.fields.valveSupply.warning,
            this.fields.filling.warning,
            this.fields.waterMeterCommunication.warning,
            this.fields.deaerator.warning,
            this.fields.vacuumDGSR.warning,
            this.fields.valveSupply.warning,
            this.fields.manometer.warning,
            this.fields.installtionKitForDPE.warning,
            this.fields.expansionSectionServiceShutOff.warning,
            this.fields.safetyValve.warning,
            this.fields.strainerOption.warning];

        return (
            <ExpandableContainer warnings={warnings} label={__('Secondary side components')}>
                <Row.Container hasOuterGutter={false}>
                    <div className="column-6 column-xs-12"
                         hidden={
                             !this.fields.valveSupply.visible &&
                             !this.fields.filling.visible &&
                             !this.fields.waterMeterCommunication.visible &&
                             !this.fields.deaerator.visible &&
                             !this.fields.vacuumDGSR.visible
                         }
                    >
                        {this.renderGenericHorizontalField(this.fields.valveSupply, {}, this.doFieldsHaveValues(model.valveSupplyDP, model.valveSupplyKVS) ? [
                            `${__('dp')}: ${model.valveSupplyDP.value} ${model.valveSupplyDP.unit}`,
                            `${__('kvs')}: ${model.valveSupplyKVS.value}`
                        ] : [])}

                        {this.shouldShowFillingInSecondarySideComponents() && this.renderGenericHorizontalField(this.fields.filling)}
                        {this.shouldShowWaterMeterCommunicationInSecondarySideComponents() && this.renderGenericHorizontalField(this.fields.waterMeterCommunication)}

                        {this.renderGenericHorizontalField(this.fields.deaerator, {}, this.doFieldsHaveValues(model.deaeratorDP, model.deaeratorKVS) ? [
                            `${__('dp')}: ${model.deaeratorDP.value} ${model.deaeratorDP.unit}`,
                            `${__('kvs')}: ${model.deaeratorKVS.value}`
                        ] : [])}

                        {this.renderGenericHorizontalField(this.fields.vacuumDGSR)}
                    </div>

                    <div className="column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.manometer)}
                        {this.renderGenericHorizontalField(this.fields.installtionKitForDPE)}
                        {this.renderGenericHorizontalField(this.fields.expansionSectionServiceShutOff)}
                        {this.shouldShowSafetyValveInSecondarySideComponents() && this.renderGenericHorizontalField(this.fields.safetyValve)}
                        {this.renderGenericHorizontalField(this.fields.strainerOption)}
                    </div>
                </Row.Container>
            </ExpandableContainer>
        )
    }

    renderPump(model) {
        const warnings = [
            this.fields.pumpManufacturer.warning,
            this.fields.pump.warning,
            this.fields.networkPressureDrop.warning,
            this.fields.requiredLiftingHeight.warning,
            this.fields.pumpFlow.warning,
            this.fields.pumpStopThermostat.warning
        ];
        let pumpModelTips = GetPumpLiftingHeightTips(model);

        var onAdditionalFiltersClick = () => this.setState(prev => ({pumpAdditionalFilterSectionVisible: !prev.pumpAdditionalFilterSectionVisible}));

        return (
            <ExpandableContainer warnings={warnings} label={__('Pump')}>
                <Row.Container hasOuterGutter={false} className="spaced-items-group-xs">
                    <div className="spaced-item column-6 column-xs-12"
                         hidden={
                             !this.fields.pumpManufacturer.visible &&
                             !this.fields.pump.visible
                         }
                    >
                        {this.getHeatingIndex() === 1 && <div style={{
                            marginLeft: '60px',
                            marginBottom: '10px'
                        }}>{this.renderFieldv2(this.fields.pumpManufacturerLocked)}</div>}
                        {this.renderGenericHorizontalField(this.fields.pumpManufacturer, {}, [], () => {
                            this.fields.pumpManufacturer.value = null;
                            store.recentlyChangedField = this.fields.pumpManufacturer.getModelPath()
                            this.props.onFormModelChange();
                        })}
                        {this.renderGenericHorizontalField(this.fields.pump, {}, pumpModelTips, () => {
                            this.fields.pump.value = null;
                            this.props.onFormModelChange();
                        }, 'has-additional-filters-container', () => {
                            return (
                                <PumpAdditionalFilters
                                    fields={this.fields}
                                    renderGenericHorizontalField={this.renderGenericHorizontalField}
                                    visible={this.state.pumpAdditionalFilterSectionVisible}
                                    onVisibleClick={onAdditionalFiltersClick}
                                />
                            )
                        })}
                        {this.renderGenericHorizontalField(this.fields.isSparePump, {renderLabel: false})}
                        {this.renderGenericHorizontalField(this.fields.isPumpSparePart, {renderLabel: false})}
                        {this.renderGenericHorizontalField(this.fields.pumpStopThermostat)}
                    </div>

                    <div className="spaced-item column-6 column-xs-12">
                        {this.renderGenericHorizontalField(this.fields.networkPressureDrop)}
                        {this.renderGenericHorizontalField(this.fields.requiredLiftingHeight)}
                        {this.renderGenericHorizontalField(this.fields.pumpFlow)}
                        {this.renderGenericHorizontalField(this.fields.bypassFilterConnections)}
                        {this.renderGenericHorizontalField(this.fields.bypassFilter)}
                        {this.renderGenericHorizontalField(this.fields.spareCartridge)}
                    </div>
                </Row.Container>
            </ExpandableContainer>
        )
    }

    static getFields(step) {
        const createSpareCartridge = () => {
            return new SpareCartridgeField('spareCartridge', step);
        }

        const createField = name => new Field(name, step)
        const createExchangerSide = name => {
            return {
                inletTemperature: new HeatExchangerField(name + '.inletTemperature', step),
                outletTemperature: new HeatExchangerField(name + '.outletTemperature', step),
                fluid: new HeatExchangerField(name + '.fluid', step),
                mixRate: new HeatExchangerField(name + '.mixRate', step),
                flow: new HeatExchangerField(name + '.flow', step),
                maxPressureDrop: new HeatExchangerField(name + '.maxPressureDrop', step),
            }
        }


        return {
            capacity: createField('capacity'),
            quickStepSetup: new QuickStepSetupField('quickStepSetup', step),
            primary: createExchangerSide('primary'),
            heatExchangerName: createField('heatExchangerName'),
            secondary: createExchangerSide('secondary'),
            plateType: createField('plateType'),
            numberOfPlates: createField('numberOfPlates'),
            margin: createField('margin'),
            marginLock: createField('marginLock'),
            secondarySideIncluded: createField('secondarySideIncluded'),
            secondaryPipeSize: createField('secondaryPipeSize'),
            secSidePipeModulePipeSize: createField('secSidePipeModulePipeSize'),
            valveSupply: createField('valveSupply'),
            filling: createField('filling'),
            waterMeterCommunication: createField('waterMeterCommunication'),
            deaerator: createField('deaerator'),
            vacuumDGSR: createField('vacuumDGSR'),
            manometer: createField('manometer'),
            installtionKitForDPE: createField('installtionKitForDPE'),
            expansionSectionServiceShutOff: createField('expansionSectionServiceShutOff'),
            safetyValve: createField('safetyValve'),
            strainerOption: createField('strainerOption'),
            pumpManufacturer: createField('pumpManufacturer'),
            pumpManufacturerLocked: createField('pumpManufacturerLocked'),
            pump: createField('pump'),
            isSparePump: new IsSparePumpField('isSparePump', step),
            isPumpSparePart: new IsPumpSparePartField('isPumpSparePart', step),
            singleTwin: createField('singleTwin'),
            adjustable: createField('adjustable'),
            dryMotor: createField('dryMotor'),
            powerSupply: createField('powerSupply'),
            pumpDPE: createField('pumpDPE'),
            networkPressureDrop: createField('networkPressureDrop'),
            requiredLiftingHeight: createField('requiredLiftingHeight'),
            pumpFlow: createField('pumpFlow'),
            bypassFilterConnections: createField('bypassFilterConnections'),
            pumpStopThermostat: createField('pumpStopThermostat'),
            bypassFilter: createField('bypassFilter'),
            spareCartridge: createSpareCartridge(),
            heatPumpConnectionsIncluded: createField('heatPumpConnectionsIncluded'),
            connectionTypeDN: createField('connectionTypeDN'),
            nonReturnValveHeatingSupply: createField('nonReturnValveHeatingSupply'),
            nonReturnValveHPReturn: createField('nonReturnValveHPReturn'),
            threeWayValve: createField('threeWayValve'),
            balancingValveHPSupply: createField('balancingValveHPSupply'),
            separateHeatExchanger: createField('separateHeatExchanger'),
            heatPumpConnectionType: createField('heatPumpConnectionType'),
            threeWayValveManufacturer: createField('threeWayValveManufacturer'),
            threeWayValveSize: createField('threeWayValveSize'),


            // These fields are referenced manually by a different step.
            flowTemperatureSensor: createField('flowTemperatureSensor'),
            returnTemperatureSensor: createField('returnTemperatureSensor'),
            jointFlowTemperatureSensor: createField('jointFlowTemperatureSensor'),
            jointReturnTemperatureSensor: createField('jointReturnTemperatureSensor'),
            returnPressureSensor: createField('returnPressureSensor'),
            differentialPressureSensor: createField('differentialPressureSensor'),
            flowThermometer: createField('flowThermometer'),
            returnThermometer: createField('returnThermometer'),
            pumpAddOn: createField('pumpAddOn'),

            is2Step: createField('is2Step'),
            temperature: new Field('design.temperature', step, false),
            pressure: new Field('design.pressure', step, false),
            pn: createField('design.pn')
        }
    }

    static getFirstVisibleField(fields) {
        return () => {
            return fields.capacity;
        };
    }

    static getLastVisibleField(fields) {
        return () => {
            if (fields.secondarySideIncluded.value) {
                return fields.pumpFlow;
            }

            return fields.secondarySideIncluded;
        };
    }
}

export class StepHeating1 extends StepHeatingBase {
    getHeatingIndex() {
        return 1
    }

    static getStepModel(model) {
        return new StepModel('heating1', model.heating1)
    }
}

export class StepHeating2 extends StepHeatingBase {
    getHeatingIndex() {
        return 2
    }

    static getStepModel(model) {
        return new StepModel('heating2', model.heating2)
    }
}

export class StepHeating3 extends StepHeatingBase {
    getHeatingIndex() {
        return 3
    }

    static getStepModel(model) {
        return new StepModel('heating3', model.heating3)
    }
}